import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Grid,
    Typography,
    CircularProgress,
    Radio,
    FormControlLabel,
    RadioGroup,
    Card,
    CardContent
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { openSignedUrl } from '../utils/s3';

function IYDataForm() {
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [existingFiles, setExistingFiles] = useState({});
    const [platforms, setPlatforms] = useState('');
    const [readOnly, setReadOnly] = useState({ name: '', email: '' });

    const [formData, setFormData] = useState({
        instagramNoOfPost: '',
        instagramInteractionScreenshot: null,
        instagramViewReachScreenshot: null,
        instagramFollowersScreenshot: null,
        youtubeNoOfPost: '',
        youtubeType: 'Post',
        youtubeVideoCost: '',
        youtubeShortsCost: '',
        youtubeViewReachScreenshot: null,
        youtubeSubscribersScreenshot: null,
        youtubeAgeGenderScreenshot: null,
        youtubeDemographyScreenshot: null,
    });

    const inputFocusStyle = {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#019383' },
        '& .MuiInputLabel-root.Mui-focused': { color: '#019383' }
    };
    const radioStyle = { '&.Mui-checked': { color: '#019383' } };

    useEffect(() => {
        if (id) fetchInfluencer();
    }, [id]);

    const fetchInfluencer = async () => {
        setFetching(true);
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get(`/influencers/${id}`);
            const inf = response.data;
            setReadOnly({ name: inf.name || '', email: inf.email || '' });
            setPlatforms(inf.socialMediaPlatforms || '');
            setFormData(prev => ({
                ...prev,
                instagramNoOfPost: inf.instagramNoOfPost || '',
                youtubeNoOfPost: inf.youtubeNoOfPost || '',
                youtubeType: inf.youtubeType || 'Post',
                youtubeVideoCost: inf.youtubeVideoCost || '',
                youtubeShortsCost: inf.youtubeShortsCost || '',
            }));
            setExistingFiles({
                instagramInteractionScreenshot: inf.instagramInteractionScreenshot,
                instagramViewReachScreenshot: inf.instagramViewReachScreenshot,
                instagramFollowersScreenshot: inf.instagramFollowersScreenshot,
                youtubeViewReachScreenshot: inf.youtubeViewReachScreenshot,
                youtubeSubscribersScreenshot: inf.youtubeSubscribersScreenshot,
                youtubeAgeGenderScreenshot: inf.youtubeAgeGenderScreenshot,
                youtubeDemographyScreenshot: inf.youtubeDemographyScreenshot,
            });
        } catch (error) {
            console.error('Failed to fetch influencer:', error);
            toast.error('Failed to load influencer data');
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            const file = files[0];
            if (!file) return;
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, [name]: 'File must be less than 5MB' }));
                return;
            }
            if (!['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type)) {
                setErrors(prev => ({ ...prev, [name]: 'Must be JPG, PNG, or PDF' }));
                return;
            }
            setErrors(prev => ({ ...prev, [name]: '' }));
            setFormData(prev => ({ ...prev, [name]: file }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (platforms.includes('instagram')) {
            if (!formData.instagramNoOfPost) newErrors.instagramNoOfPost = 'Instagram No. Of Post is required';
            if (!formData.instagramInteractionScreenshot && !existingFiles.instagramInteractionScreenshot) newErrors.instagramInteractionScreenshot = 'Instagram Interactions Screenshot is required';
            if (!formData.instagramViewReachScreenshot && !existingFiles.instagramViewReachScreenshot) newErrors.instagramViewReachScreenshot = 'Instagram View & Reach Screenshot is required';
            if (!formData.instagramFollowersScreenshot && !existingFiles.instagramFollowersScreenshot) newErrors.instagramFollowersScreenshot = 'Instagram Followers Screenshot is required';
        }

        if (platforms.includes('youtube')) {
            if (!formData.youtubeNoOfPost) newErrors.youtubeNoOfPost = 'YouTube No. Of Post is required';
            if (formData.youtubeType === 'Post' && !formData.youtubeVideoCost) newErrors.youtubeVideoCost = 'YouTube Video Cost is required';
            if (formData.youtubeType === 'Shorts' && !formData.youtubeShortsCost) newErrors.youtubeShortsCost = 'YouTube Shorts Cost is required';
            if (!formData.youtubeViewReachScreenshot && !existingFiles.youtubeViewReachScreenshot) newErrors.youtubeViewReachScreenshot = 'YouTube Views & Reach Screenshot is required';
            if (!formData.youtubeSubscribersScreenshot && !existingFiles.youtubeSubscribersScreenshot) newErrors.youtubeSubscribersScreenshot = 'YouTube Subscribers Screenshot is required';
            if (!formData.youtubeAgeGenderScreenshot && !existingFiles.youtubeAgeGenderScreenshot) newErrors.youtubeAgeGenderScreenshot = 'YouTube Age, Gender Screenshot is required';
            if (!formData.youtubeDemographyScreenshot && !existingFiles.youtubeDemographyScreenshot) newErrors.youtubeDemographyScreenshot = 'YouTube Demography Screenshot is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error('Please fill all required fields');
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== '') {
                    fd.append(key, formData[key]);
                }
            });

            fd.append('ocr_status', 'completed');

            const { default: api } = await import('../services/api');
            await api.put(`/influencers/${id}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Update ocr_status to completed
            // await api.put(`/influencers/${id}`, { ocr_status: 'completed' });
            toast.success('Updated successfully!');
            setSubmitted(true);
        } catch (error) {
            console.error('Update error:', error);
            if (error.response?.data) {
                const errorData = error.response.data;
                const errMsg = errorData.error || errorData.message || 'Failed to update';
                toast.error(errMsg);
                if (errorData.validationErrors && errorData.validationErrors.length > 0) {
                    const fieldErrors = {};
                    errorData.validationErrors.forEach(err => {
                        fieldErrors[err.field] = err.message;
                    });
                    setErrors(prev => ({ ...prev, ...fieldErrors }));
                } else if (errorData.field) {
                    setErrors(prev => ({ ...prev, [errorData.field]: errMsg }));
                }
            } else {
                toast.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#019383' }} />
            </Box>
        );
    }

    if (submitted) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', p: 3 }}>
                <Card sx={{ maxWidth: 500, textAlign: 'center', boxShadow: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                        <Typography variant="h4" sx={{ color: '#019383', mb: 2, fontWeight: 600 }}>
                            Thank You{readOnly.name ? `, ${readOnly.name}` : ''}!
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
                            Your data has been updated successfully
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8 }}>
                            We appreciate your interest. Our team will review your application and get back to you soon.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const renderFileUpload = (label, name, sampleUrl, sampleLabel) => (
        <Grid item xs={12} sm={6} key={name}>
            <Typography variant="body2" sx={{ mb: 1 }}>{label}</Typography>
            {existingFiles[name] && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    Current: <a href="#" onClick={(e) => { e.preventDefault(); openSignedUrl(existingFiles[name]); }}>View File</a>
                </Typography>
            )}
            <TextField
                fullWidth type="file" name={name} onChange={handleChange}
                inputProps={{ accept: 'image/jpeg,image/jpg,image/png,application/pdf' }}
                error={!!errors[name]} helperText="Max 5MB, JPG/PNG/PDF only" sx={inputFocusStyle}
            />
            {errors[name] && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>{errors[name]}</Typography>}
            {sampleUrl && (
                <Button variant="contained" sx={{ bgcolor: '#019383', mt: 1, '&:hover': { bgcolor: '#4a7bd9' } }}
                    onClick={() => window.open(`${process.env.REACT_APP_BASE_URL}/images/${sampleUrl}`, '_blank')}>
                    {sampleLabel}
                </Button>
            )}
        </Grid>
    );

    return (
        <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
            <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ color: '#019383', mb: 2 }}>Basic Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Name" value={readOnly.name} InputProps={{ readOnly: true }} sx={inputFocusStyle} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Email" value={readOnly.email} InputProps={{ readOnly: true }} sx={inputFocusStyle} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Social Media Platforms" value={platforms} InputProps={{ readOnly: true }} sx={inputFocusStyle} />
                    </Grid>

                    {platforms.includes('instagram') && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#019383', mb: 2, mt: 2 }}>Instagram Details</Typography>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField fullWidth label="Instagram No. Of Post (Past 30 days)" name="instagramNoOfPost" type="number"
                                    value={formData.instagramNoOfPost} onChange={handleChange}
                                    error={!!errors.instagramNoOfPost} helperText={errors.instagramNoOfPost} sx={inputFocusStyle} />
                            </Grid>
                            {renderFileUpload("Instagram Interactions Screenshot", "instagramInteractionScreenshot",
                                "Interactions_img.jpg", "Interactions Sample")}
                            {renderFileUpload("Instagram View & Reach Screenshot", "instagramViewReachScreenshot",
                                "view_reach_img.jpg", "View & Reach Sample")}
                            {renderFileUpload("Instagram Followers Screenshot", "instagramFollowersScreenshot",
                                "InstaFollowers.jpg", "Followers Sample")}
                        </>
                    )}

                    {platforms.includes('youtube') && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#019383', mb: 2, mt: 2 }}>YouTube Details</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1 }}>Youtube Type:</Typography>
                                <RadioGroup row name="youtubeType" value={formData.youtubeType} onChange={handleChange}>
                                    <FormControlLabel value="Post" control={<Radio sx={radioStyle} />} label="Post" />
                                    <FormControlLabel value="Shorts" control={<Radio sx={radioStyle} />} label="Shorts" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="YouTube No. Of Post (Past 30 days)" name="youtubeNoOfPost" type="number"
                                    value={formData.youtubeNoOfPost} onChange={handleChange}
                                    error={!!errors.youtubeNoOfPost} helperText={errors.youtubeNoOfPost} sx={inputFocusStyle} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {formData.youtubeType === 'Post' && (
                                    <TextField fullWidth label="YouTube Video Cost" name="youtubeVideoCost" type="number"
                                        value={formData.youtubeVideoCost} onChange={handleChange}
                                        error={!!errors.youtubeVideoCost} helperText={errors.youtubeVideoCost} sx={inputFocusStyle} />
                                )}
                                {formData.youtubeType === 'Shorts' && (
                                    <TextField fullWidth label="YouTube Shorts Cost" name="youtubeShortsCost" type="number"
                                        value={formData.youtubeShortsCost} onChange={handleChange}
                                        error={!!errors.youtubeShortsCost} helperText={errors.youtubeShortsCost} sx={inputFocusStyle} />
                                )}
                            </Grid>
                            {renderFileUpload("YouTube Views & Reach Screenshot", "youtubeViewReachScreenshot",
                                "YouTube - Views, Reach, Like, comment, share.jpeg", "YouTube Views & Reach Sample")}
                            {renderFileUpload("YouTube Subscribers Screenshot", "youtubeSubscribersScreenshot",
                                "YouTube - Subscriber.jpeg", "YouTube Subscriber Sample")}
                            {renderFileUpload("YouTube - Age, Gender Screenshot", "youtubeAgeGenderScreenshot",
                                "YouTube - age, gender.jpeg", "YouTube Age, Gender Sample")}
                            {renderFileUpload("YouTube - Demography Screenshot", "youtubeDemographyScreenshot",
                                "YouTube - Locations.jpeg", "YouTube Demography Sample")}
                        </>
                    )}

                    <Grid item xs={12}>
                        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}
                            sx={{ bgcolor: '#019383', '&:hover': { bgcolor: '#4a7bd9' }, mt: 2, py: 1.5 }}>
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default IYDataForm;
